import type { PocInputValidity } from 'commons/types';
import { useTranslation } from 'core/i18n/i18n';
import React, { useEffect } from 'react';
import { QcProperties } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-device-configuration-status-tracking/settings-device-configuration-status-tracking.model';
import { INVALID_STATUS_TRACKING_STATE, VALID_STATUS_TRACKING_STATE } from '../../constants';

type QcStatusTrackingCardValidation = 'warning-less-than-alert' | 'warning-more-than-alert';

interface UseQcStatusTrackingCardValidationProps {
    onValidityChange?: (isValid: PocInputValidity) => void;
}

export const useQcStatusTrackingCardValidation = ({ onValidityChange }: UseQcStatusTrackingCardValidationProps) => {
    const [validity, setValidity] = React.useState<PocInputValidity>(VALID_STATUS_TRACKING_STATE);
    const [internalCustomValidation, setInternalCustomValidation] = React.useState<QcStatusTrackingCardValidation>();
    const { t } = useTranslation();

    const checkValidation = (values: QcProperties): PocInputValidity => {
        let error: QcStatusTrackingCardValidation | null = null;
        if (values.days > 365) {
            error = 'warning-more-than-alert';
        } else if (values.days < 0) {
            error = 'warning-less-than-alert';
        }

        if (error) {
            setInternalCustomValidation(error);
            setValidity(INVALID_STATUS_TRACKING_STATE);
            return INVALID_STATUS_TRACKING_STATE;
        }

        setInternalCustomValidation(undefined);
        setValidity(VALID_STATUS_TRACKING_STATE);
        return VALID_STATUS_TRACKING_STATE;
    };

    const getValidationMessageByType = React.useCallback(() => {
        const validationMessageByType = {
            'warning-less-than-alert': t(
                'settings.devices.device-settings-configuration-status-tracking.qc.validity.alertLessThanWarning'
            ),
            'warning-more-than-alert': t(
                'settings.devices.device-settings-configuration-status-tracking.qc.validity.alertMoreThanWarning'
            ),
        };
        return validationMessageByType[internalCustomValidation!] || '';
    }, [t, internalCustomValidation]);

    useEffect(() => {
        onValidityChange?.(validity);
    }, [validity]);

    return { validity, getValidationMessageByType, checkValidation };
};
